import { useEffect, useState, type FormEvent } from 'react';

import { ModalCloseButton } from './InfoModals';
import {
  AI_AGENT_LABELS,
  SKILL_LABELS,
  formatLabel,
  formatModelLabel,
  type LaunchState,
  type LoadState,
  type PricingEstimateResponse,
  type PricingPlan,
  type PricingPlanEstimate,
  type PricingProvider,
  type PricingProviderModelCost,
  type ScanAcceptedResponse
} from './workflowShared';

interface PricingStepProps {
  hasAcceptedScan: boolean;
  scanResponse: ScanAcceptedResponse | null;
  pricingEstimateState: LoadState;
  pricingEstimateError: string;
  pricingEstimate: PricingEstimateResponse | null;
  pricingEstimateFallback?: string;
  selectedPlan: PricingPlan;
  launchState: LaunchState;
  launchError: string;
  onViewRun: (runId: string) => void;
}

interface ResultEmailPromptModalProps {
  isOpen: boolean;
  initialEmail: string;
  launchState: LaunchState;
  onCancel: () => void;
  onSubmit: (email: string) => void;
}

const formatUsd = (value: number, options: Intl.NumberFormatOptions = {}): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options
  }).format(value);
};

const formatCompactUsd = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: value < 1 ? 3 : 2,
    maximumFractionDigits: value < 1 ? 3 : 2
  }).format(value);
};

const formatNumber = (value: number): string => {
  return new Intl.NumberFormat('en-US').format(value);
};

const formatCompactNumber = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1
  }).format(value);
};

const formatProviderLabel = (provider: PricingProvider): string => {
  if (provider === 'anthropic') {
    return 'Claude';
  }

  if (provider === 'openai') {
    return 'OpenAI';
  }

  if (provider === 'zai') {
    return 'Z.ai';
  }

  return 'Unknown provider';
};

const formatProviderRate = (entry: PricingProviderModelCost): string => {
  return `${formatCompactUsd(entry.inputTokenRateUsdPerMillion)}/M input`;
};

const formatPricingType = (pricingType: PricingEstimateResponse['pricingType']): string => {
  return pricingType === 'web-api' ? 'Web/API source pentest' : 'Smart contract audit';
};

const formatSkillSummary = (
  pricingType: PricingEstimateResponse['pricingType'],
  plan: PricingPlanEstimate
): string => {
  const toolCount = plan.selectedSkills?.length || plan.selectedSkillCount;
  const modelCount = plan.selectedModelCount;
  const toolLabel = toolCount === 1 ? 'tool' : 'tools';
  const modelLabel = modelCount === 1 ? 'model' : 'models';

  return `${formatPricingType(pricingType)} - ${formatNumber(toolCount)} ${toolLabel}, ${formatNumber(modelCount)} ${modelLabel}`;
};

const getSelectedModelEntries = (pricingEstimate: PricingPlanEstimate): { aiAgent: string; model: string }[] => {
  const selectedModels = (pricingEstimate.selectedAiAgentModels ?? [])
    .map((selection) => ({
      aiAgent: selection.aiAgent.trim(),
      model: selection.model?.trim() ?? ''
    }))
    .filter((selection) => selection.aiAgent && selection.model);

  if (selectedModels.length > 0) {
    return selectedModels;
  }

  return pricingEstimate.providerModelCosts
    .map((entry) => ({
      aiAgent: entry.aiAgent.trim(),
      model: entry.model.trim()
    }))
    .filter((entry) => entry.aiAgent && entry.model);
};

const isValidResultEmail = (value: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
};

function ResultEmailPromptModal({
  isOpen,
  initialEmail,
  launchState,
  onCancel,
  onSubmit
}: ResultEmailPromptModalProps) {
  const [email, setEmail] = useState(initialEmail);
  const [error, setError] = useState('');
  const isSubmitting = launchState === 'submitting';

  useEffect(() => {
    if (isOpen) {
      setEmail(initialEmail);
      setError('');
    }
  }, [initialEmail, isOpen]);

  if (!isOpen) {
    return null;
  }

  const submitEmail = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedEmail = email.trim();

    if (!isValidResultEmail(trimmedEmail)) {
      setError('Enter a valid email address.');
      return;
    }

    onSubmit(trimmedEmail);
  };

  const cancel = () => {
    if (!isSubmitting) {
      onCancel();
    }
  };

  return (
    <div className="modal-overlay" onClick={cancel}>
      <form
        className="modal-content pricing-email-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pricing-email-title"
        onClick={(event) => event.stopPropagation()}
        onSubmit={submitEmail}
      >
        <div className="modal-header">
          <h2 id="pricing-email-title">Get scan results</h2>
          <ModalCloseButton onClose={cancel} disabled={isSubmitting} />
        </div>

        <div className="modal-body pricing-email-body">
          <p className="pricing-email-copy">
            Add an email address so we can send the scan result when it is ready.
          </p>

          <label className="field">
            <span>Email</span>
            <input
              type="email"
              value={email}
              autoComplete="email"
              autoFocus
              placeholder="security@example.com"
              disabled={isSubmitting}
              onChange={(event) => {
                setEmail(event.target.value);
                setError('');
              }}
            />
          </label>

          {error && (
            <p className="pricing-email-error" role="alert">{error}</p>
          )}
        </div>

        <div className="modal-footer pricing-email-footer">
          <button
            type="button"
            className="secondary-button"
            disabled={isSubmitting}
            onClick={cancel}
          >
            Cancel
          </button>
          <button type="submit" className="primary-button" disabled={isSubmitting}>
            {isSubmitting ? 'Starting...' : 'Start scan'}
          </button>
        </div>
      </form>
    </div>
  );
}

function PricingStep({
  hasAcceptedScan,
  scanResponse,
  pricingEstimateState,
  pricingEstimateError,
  pricingEstimate,
  pricingEstimateFallback,
  selectedPlan,
  launchState,
  launchError,
  onViewRun
}: PricingStepProps) {
  // Older backends only return the full plan at the top level.
  const plan: PricingPlanEstimate | null = pricingEstimate?.plans?.[selectedPlan] ?? pricingEstimate;
  const selectedTools = plan?.selectedSkills ?? [];
  const selectedModels = plan ? getSelectedModelEntries(plan) : [];
  const isFreePlan = plan?.totalUsd === 0;

  if (hasAcceptedScan) {
    const runId = scanResponse?.runId;

    return (
      <div className="pricing-launch-complete">
        <div className="pricing-launch-run-card" role="status" aria-label="Scan run created">
          <div className="pricing-launch-run-id">
            <span>Run ID</span>
            <strong>{runId || 'Unavailable'}</strong>
          </div>

          {runId ? (
            <a
              className="primary-button pricing-launch-run-link"
              href={`/run/${encodeURIComponent(runId)}`}
              onClick={(event) => {
                event.preventDefault();
                onViewRun(runId);
              }}
            >
              View run
            </a>
          ) : (
            <button className="primary-button pricing-launch-run-link" type="button" disabled>
              View run
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      {pricingEstimateState === 'loading' && (
        <div className="pricing-estimate" role="status">
          <div className="pricing-loading-panel">
            <span className="pricing-muted">Estimating price...</span>
          </div>
        </div>
      )}

      {pricingEstimateState === 'error' && (
        <div className="status-box error" role="alert">
          <span>Price estimate failed.</span>
          <span>{pricingEstimateError}</span>
        </div>
      )}

      {pricingEstimateState === 'ready' && pricingEstimate && plan && (
        <div className="pricing-estimate">
          <section className="pricing-invoice" aria-label="Scan pricing">
            <dl className="pricing-invoice-list">
              <div className="pricing-invoice-row">
                <dt>Codebase</dt>
                <dd>
                  {formatNumber(pricingEstimate.codebase.nonCommentLineCount)} LOC /{' '}
                  {formatNumber(pricingEstimate.codebase.countedFileCount)} files /{' '}
                  {formatCompactNumber(pricingEstimate.tokenEstimate.estimatedCodebaseTokens)} source tokens
                </dd>
              </div>
              <div className="pricing-invoice-row">
                <dt>Run contents</dt>
                <dd>{formatSkillSummary(pricingEstimate.pricingType, plan)}</dd>
              </div>
              <div className="pricing-invoice-row">
                <dt>Estimated model usage</dt>
                <dd>{formatCompactNumber(plan.tokenEstimate.totalEstimatedInputTokens)} input tokens</dd>
              </div>
              <div className="pricing-invoice-row pricing-invoice-row-stacked">
                <dt>Price breakdown</dt>
                <dd>
                  {plan.providerModelCosts.map((entry) => (
                    <span className="pricing-invoice-line" key={`${entry.aiAgent}-${entry.model}`}>
                      <span>
                        {formatProviderLabel(entry.provider)} / {formatModelLabel(entry.model)}
                        {!isFreePlan && ` / ${formatProviderRate(entry)}`}
                      </span>
                      <strong>{isFreePlan ? 'Included' : formatUsd(entry.apiCostUsd)}</strong>
                    </span>
                  ))}
                  {!isFreePlan && (
                    <span className="pricing-invoice-line pricing-invoice-fee-line">
                      <span>Havu platform fee</span>
                      <strong>{formatUsd(plan.platformFeeUsd)}</strong>
                    </span>
                  )}
                </dd>
              </div>
              <div className="pricing-invoice-row pricing-invoice-row-total">
                <dt>Total due</dt>
                <dd>{formatUsd(plan.totalUsd)}</dd>
              </div>
            </dl>

            <details className="pricing-included-details">
              <summary>
                <span>Included in this run</span>
                <small>{formatSkillSummary(pricingEstimate.pricingType, plan)}</small>
              </summary>
              <div className="pricing-included-grid">
                <div>
                  <span className="pricing-included-label">Tools</span>
                  <div className="pricing-included-list" aria-label="Included tools">
                    {selectedTools.length > 0 ? (
                      selectedTools.map((skill) => (
                        <span className="pricing-included-pill" key={skill}>
                          {formatLabel(skill, SKILL_LABELS)}
                        </span>
                      ))
                    ) : (
                      <span className="pricing-included-muted">
                        {formatNumber(plan.selectedSkillCount)} included tools
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <span className="pricing-included-label">Models</span>
                  <div className="pricing-included-list" aria-label="Included models">
                    {selectedModels.length > 0 ? (
                      selectedModels.map((selection) => (
                        <span className="pricing-included-pill" key={`${selection.aiAgent}-${selection.model}`}>
                          {formatLabel(selection.aiAgent, AI_AGENT_LABELS)} / {formatModelLabel(selection.model)}
                        </span>
                      ))
                    ) : (
                      <span className="pricing-included-muted">
                        {formatNumber(plan.selectedModelCount)} included models
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </details>

            <p className="pricing-estimate-note">
              We estimate token consumption from codebase size and previous similar runs. The scan price uses input tokens only.
            </p>
          </section>
        </div>
      )}

      {pricingEstimateState === 'ready' && !pricingEstimate && pricingEstimateFallback && (
        <div className="pricing-panel">
          <div className="pricing-total-row">
            <span>Price of scan</span>
            <strong>{pricingEstimateFallback}</strong>
          </div>
        </div>
      )}

      {launchState === 'error' && (
        <div className="status-box error" role="alert">
          <span>Scan request failed.</span>
          <span>{launchError}</span>
        </div>
      )}
    </>
  );
}

export { ResultEmailPromptModal };
export default PricingStep;
